import {
  Alert,
  Button,
  Select,
  type SelectOptionElement,
} from '@odin-labs/components';
import {
  useGetAvailableSiteControllersQuery,
  type GetAvailableSiteControllersQuery,
} from 'apollo/generated/client-operations';
import React from 'react';

type Controller =
  GetAvailableSiteControllersQuery['availableSiteControllers']['nodes'][number];
type PickerProps = {
  siteId: string;
  serialNumber: string;
  disabled: boolean;
  onChoose: (serialNumber: string) => void;
};

/** Mounted only after the admin explicitly asks to read Avigilon's inventory. */
function FetchedSiteControllers({
  siteId,
  serialNumber,
  disabled,
  onChoose,
}: PickerProps): React.ReactElement {
  const [after, setAfter] = React.useState<string | null>(null);
  const [controllers, setControllers] = React.useState<Controller[]>([]);
  const [pageInfo, setPageInfo] = React.useState<{
    endCursor: string | null;
    hasNextPage: boolean;
  }>({ endCursor: null, hasNextPage: false });
  const { data, loading, error, refetch } = useGetAvailableSiteControllersQuery(
    {
      variables: { siteId, first: 50, after },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );
  React.useEffect(() => {
    if (loading || error || !data) return;
    const page = data.availableSiteControllers;
    setControllers((previous) => [
      ...new Map(
        [...previous, ...page.nodes].map((node) => [node.id, node]),
      ).values(),
    ]);
    setPageInfo({
      endCursor: page.pageInfo.endCursor ?? null,
      hasNextPage: page.pageInfo.hasNextPage,
    });
  }, [data, loading, error]);
  const options = controllers.map(({ id, name, serialNumber: serial }) => ({
    value: id,
    label: `${name} · ${serial}`,
  }));
  const selected = controllers.find(
    (controller) => controller.serialNumber === serialNumber,
  );

  return (
    <div className="odin-space-y-3">
      {loading && <Button text="Fetch from Avigilon" withSpinner disabled />}
      {error && (
        <>
          <Alert
            type="danger"
            text="Could not fetch controllers from Avigilon. Enter the serial number manually or try again."
          />
          <Button
            text="Try fetching again"
            disabled={disabled || loading}
            onClick={(): void => {
              refetch().catch((): undefined => undefined);
            }}
          />
        </>
      )}
      <Select
        name="availableSiteController"
        label="Controller at this Avigilon site"
        placeholder="Search by name or serial number"
        options={options}
        value={options.find(({ value }) => value === selected?.id) ?? null}
        isSearchable
        isClearable={false}
        isLoading={loading}
        isDisabled={disabled || (loading && controllers.length === 0)}
        onChange={(option: SelectOptionElement | null) => {
          const controller = controllers.find(({ id }) => id === option?.value);
          if (controller) onChoose(controller.serialNumber);
        }}
      />
      {!loading && !error && controllers.length === 0 && (
        <p>
          No controllers are available at the linked Avigilon site. You can
          enter a serial number manually.
        </p>
      )}
      {pageInfo.hasNextPage && pageInfo.endCursor && (
        <Button
          text="Load more controllers"
          disabled={disabled || loading}
          onClick={() => setAfter(pageInfo.endCursor)}
        />
      )}
    </div>
  );
}

/** This wrapper has no Apollo hook, so opening the drawer never reads inventory. */
export function SiteControllerPicker(props: PickerProps): React.ReactElement {
  const { disabled } = props;
  const [requested, setRequested] = React.useState(false);
  return requested ? (
    <FetchedSiteControllers {...props} />
  ) : (
    <Button
      text="Fetch from Avigilon"
      disabled={disabled}
      onClick={() => setRequested(true)}
    />
  );
}
